import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CollaboratorsHistoryEducationsService from "../services/api/collaborators/CollaboratorsHistoryEducations.service";
import {
  CERTIFICATIONS_PAGE_SIZE,
  COLLABORATORS_PAGE_SIZE,
  COURSES_PAGE_SIZE,
  RefreshReactQuery,
} from "../constants/AppConstants";
import { useNotification } from "./notification.hook";
import {
  DATA_ADDED_SUCCESS_LABEL,
  DATA_DELETED_SUCCESS_LABEL,
  DATA_UPDATED_SUCCESS_LABEL,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
} from "../constants/Dictionary";
import {
  AddCollaboratorsHistoryCertification,
  AddCollaboratorsHistoryCourse,
  AddUpdateCollaboratorsEducation,
  UpdateCollaboratorsHistoryCertification,
  UpdateCollaboratorsHistoryCourse,
} from "../types/api/collaborators.type";
import CollaboratorsHistoryCoursesService from "../services/api/collaborators/CollaboratorsHistoryCourses.service";
import CollaboratorsHistoryCertificateOfCoursesService from "../services/api/collaborators/CollaboratorsHistoryCertificateOfCourses.service";
import CollaboratorsHistoryCertificationsService from "../services/api/collaborators/CollaboratorsHistoryCertifications.service";
import CollaboratorsHistoryCertificateOfCertificationsService from "../services/api/collaborators/CollaboratorsHistoryCertificateOfCertfications.service";
import CoursesService from "../services/api/courses/Courses.service";
import CertificationsService from "../services/api/certifications/Certifications.service";
import { useEffect } from "react";

interface useHistoryProps {
  profileId: number;
  pageNumber: number;
  searchDebouncedValue: string;
}

export const useEducationHistory = ({
  profileId,
  pageNumber,
  searchDebouncedValue,
}: useHistoryProps) => {
  const { notification } = useNotification();

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      `get-collaborator-history-educations-${profileId}`,
      pageNumber,
      searchDebouncedValue,
    ],
    queryFn: ({ signal }) =>
      CollaboratorsHistoryEducationsService.list(
        profileId,
        COLLABORATORS_PAGE_SIZE,
        pageNumber,
        [
          ...(searchDebouncedValue
            ? [{ key: "search", value: [searchDebouncedValue] }]
            : []),
        ],
        signal,
      ),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

export const useEducationHistoryDelete = (profileId: number) => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (selectedEducationId: number) =>
      CollaboratorsHistoryEducationsService.delete(
        profileId,
        selectedEducationId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`get-collaborator-history-educations-${profileId}`],
      });
      notification.success(DATA_DELETED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(error);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

export const useEducationHistoryUpdate = (profileId: number) => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({
      educationId,
      body,
    }: {
      educationId: number;
      body: AddUpdateCollaboratorsEducation;
    }) =>
      CollaboratorsHistoryEducationsService.update(
        profileId,
        educationId,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`get-collaborator-history-educations-${profileId}`],
      });
      notification.success(DATA_UPDATED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(`Error updating course: ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

export const useEducationHistoryAdd = (profileId: number) => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (body: AddUpdateCollaboratorsEducation) =>
      CollaboratorsHistoryEducationsService.add(profileId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`get-collaborator-history-educations-${profileId}`],
      });
      notification.success(DATA_ADDED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(`Error updating course: ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

export const useCourseHistory = ({
  profileId,
  pageNumber,
  searchDebouncedValue,
}: useHistoryProps) => {
  const { notification } = useNotification();

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      `get-collaborator-history-courses-${profileId}`,
      pageNumber,
      searchDebouncedValue,
    ],
    queryFn: ({ signal }) =>
      CollaboratorsHistoryCoursesService.list(
        profileId,
        COLLABORATORS_PAGE_SIZE,
        pageNumber,
        [
          ...(searchDebouncedValue
            ? [{ key: "search", value: [searchDebouncedValue] }]
            : []),
        ],
        signal,
      ),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

export const useCourseList = ({
  profileId,
  pageNumber,
  searchDebouncedValue,
}: useHistoryProps) => {
  const { notification } = useNotification();

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      `get-collaborator-history-course-list-${profileId}`,
      pageNumber,
      searchDebouncedValue,
    ],
    queryFn: () =>
      CoursesService.list(COURSES_PAGE_SIZE, pageNumber, [
        ...(searchDebouncedValue
          ? [{ key: "search", value: [searchDebouncedValue] }]
          : []),
      ]),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading };
};

export const useCourseHistoryDelete = (profileId: number) => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (selectedEducationId: number) =>
      CollaboratorsHistoryCoursesService.delete(profileId, selectedEducationId),
    onSuccess: () => {
      notification.success(DATA_DELETED_SUCCESS_LABEL, undefined, "toast");
      queryClient.invalidateQueries({
        queryKey: [`get-collaborator-history-courses-${profileId}`],
      });
    },
    onError: (error) => {
      console.error(error);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

interface useHistoryCertificateProps {
  profileId: number;
  isOpen: boolean;
  certificateId?: number;
}

export const useCourseHistoryCertificate = ({
  profileId,
  isOpen,
  certificateId,
}: useHistoryCertificateProps) => {
  const { notification } = useNotification();

  const { data, isError } = useQuery({
    queryKey: [
      `get-collaborator-history-course-certificate-${certificateId}-${profileId}`,
    ],
    queryFn: () =>
      CollaboratorsHistoryCertificateOfCoursesService.get(
        profileId,
        certificateId!,
      ),
    enabled: !!certificateId && isOpen,
    staleTime: RefreshReactQuery.INFINITY,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data };
};

interface FormDataCourse {
  courseId?: number | null;
  name?: string;
  year: number | null;
  certificateFile: {
    file?: File | null;
    name?: string | null;
  } | null;
}

export const useCourseHistoryUpdate = (profileId: number) => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      courseId,
      updateBody,
      data,
    }: {
      courseId: number;
      updateBody: UpdateCollaboratorsHistoryCourse;
      data: FormDataCourse;
    }) => {
      await CollaboratorsHistoryCoursesService.update(
        profileId,
        courseId,
        updateBody,
      );
      if (data.certificateFile && data.certificateFile.file) {
        await CollaboratorsHistoryCertificateOfCoursesService.update(
          profileId,
          courseId,
          { file: data.certificateFile.file },
        );
      }
      if (data.certificateFile === null) {
        await CollaboratorsHistoryCertificateOfCoursesService.delete(
          profileId,
          courseId,
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`get-collaborator-history-courses-${profileId}`],
      });
      notification.success(DATA_UPDATED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(`Error updating course: ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

export const useCourseHistoryAdd = (profileId: number) => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      addBody,
      data,
    }: {
      addBody: AddCollaboratorsHistoryCourse;
      data: FormDataCourse;
    }) => {
      const response = await CollaboratorsHistoryCoursesService.add(
        profileId,
        addBody,
      );
      if (data.certificateFile && data.certificateFile.file) {
        await CollaboratorsHistoryCertificateOfCoursesService.update(
          profileId,
          response.id,
          { file: data.certificateFile.file },
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`get-collaborator-history-courses-${profileId}`],
      });
      notification.success(DATA_ADDED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(`Error updating course: ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

interface useCertificationHistoryProps {
  profileId: number;
  pageNumber: number;
  searchDebouncedValue: string;
  status: string | null;
}

export const useCertificationHistory = ({
  profileId,
  pageNumber,
  searchDebouncedValue,
  status,
}: useCertificationHistoryProps) => {
  const { notification } = useNotification();

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      `get-collaborator-history-certifications-${profileId}`,
      pageNumber,
      searchDebouncedValue,
      status,
    ],
    queryFn: () =>
      CollaboratorsHistoryCertificationsService.list(
        profileId,
        COLLABORATORS_PAGE_SIZE,
        pageNumber,
        [
          ...(searchDebouncedValue
            ? [{ key: "search", value: [searchDebouncedValue] }]
            : []),
          ...(status ? [{ key: "status", value: [status] }] : []),
        ],
      ),
    staleTime: RefreshReactQuery.INFINITY,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

export const useCertificationHistoryCertificate = ({
  profileId,
  isOpen,
  certificateId,
}: useHistoryCertificateProps) => {
  const { notification } = useNotification();

  const { data, isError } = useQuery({
    queryKey: [
      `get-collaborator-history-certification-certificate-${certificateId}-${profileId}`,
    ],
    queryFn: () =>
      CollaboratorsHistoryCertificateOfCertificationsService.get(
        profileId,
        certificateId!,
      ),
    enabled: !!certificateId && isOpen,
    staleTime: RefreshReactQuery.INFINITY,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data };
};

export const useCertificationList = ({
  profileId,
  pageNumber,
  searchDebouncedValue,
}: useHistoryProps) => {
  const { notification } = useNotification();

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      `get-collaborator-certification-list-${profileId}`,
      pageNumber,
      searchDebouncedValue,
    ],
    queryFn: () =>
      CertificationsService.list(CERTIFICATIONS_PAGE_SIZE, pageNumber, [
        ...(searchDebouncedValue
          ? [{ key: "search", value: [searchDebouncedValue] }]
          : []),
      ]),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

interface FormDataCertification {
  certificationId?: number | null;
  name?: string;
  deliveryDate: Date | null;
  expirationDate?: Date | null;
  certificateFile: {
    file?: File | null;
    name?: string | null;
  } | null;
}

export const useCertificationHistoryUpdate = (profileId: number) => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      certificationId,
      updateBody,
      data,
    }: {
      certificationId: number;
      updateBody: UpdateCollaboratorsHistoryCertification;
      data: FormDataCertification;
    }) => {
      await CollaboratorsHistoryCertificationsService.update(
        profileId,
        certificationId,
        updateBody,
      );
      if (data.certificateFile && data.certificateFile.file) {
        await CollaboratorsHistoryCertificateOfCertificationsService.update(
          profileId,
          certificationId,
          { file: data.certificateFile.file },
        );
      }
      if (data.certificateFile === null) {
        await CollaboratorsHistoryCertificateOfCertificationsService.delete(
          profileId,
          certificationId,
        );
      }
    },
    onSuccess: () => {
      notification.success(DATA_UPDATED_SUCCESS_LABEL, undefined, "toast");
      queryClient.invalidateQueries({
        queryKey: [`get-collaborator-history-certifications-${profileId}`],
      });
    },
    onError: (error) => {
      console.error(`Error updating course: ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

export const useCertificationHistoryAdd = (profileId: number) => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      addBody,
      data,
    }: {
      addBody: AddCollaboratorsHistoryCertification;
      data: FormDataCertification;
    }) => {
      const response = await CollaboratorsHistoryCertificationsService.add(
        profileId,
        addBody,
      );
      if (data.certificateFile?.file) {
        await CollaboratorsHistoryCertificateOfCertificationsService.update(
          profileId,
          response.id,
          { file: data.certificateFile.file },
        );
      }
    },
    onSuccess: () => {
      notification.success(DATA_ADDED_SUCCESS_LABEL, undefined, "toast");
      queryClient.invalidateQueries({
        queryKey: [`get-collaborator-history-certifications-${profileId}`],
      });
    },
    onError: (error) => {
      console.error(`Error updating course: ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

export const useCertificationHistoryDelete = (profileId: number) => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (selectedCertificationId: number) =>
      CollaboratorsHistoryCertificationsService.delete(
        profileId,
        selectedCertificationId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`get-collaborator-history-certifications-${profileId}`],
      });
      notification.success(DATA_DELETED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(error);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};
